import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { FaCheck, FaEdit, FaTrash } from "react-icons/fa";

export default function Onboarding() {
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
  });
  const [editingId, setEditingId] = useState(null);
  const token = localStorage.getItem("token");

  // Fetch employees
  const fetchEmployees = useCallback(async () => {
    try {
      const res = await axios.get("https://smart-hr.shopattropical.com/api/hr/employees", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEmployees(res.data);
    } catch (err) {
      console.error(err);
      alert("Failed to fetch employees");
    }
  }, [token]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        // Update employee
        await axios.put(
          `https://smart-hr.shopattropical.com/api/hr/update/${editingId}`,
          form,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setEditingId(null);
        alert("Employee updated!");
      } else {
        // Create employee
        await axios.post(
          "https://smart-hr.shopattropical.com/api/hr/create-employee",
          form,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert("Employee created!");
      }
      setForm({ first_name: "", last_name: "", email: "", password: "" });
      fetchEmployees();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Failed to submit");
    }
  };

  const handleApprove = async (id) => {
    try {
      await axios.put(
        `https://smart-hr.shopattropical.com/api/hr/approve/${id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("Employee approved!");
      fetchEmployees();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Failed to approve employee");
    }
  };

  const handleEdit = (emp) => {
    setForm({
      first_name: emp.first_name,
      last_name: emp.last_name,
      email: emp.email,
      password: "",
    });
    setEditingId(emp.id);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure to delete this employee?")) return;
    try {
      await axios.delete(`https://smart-hr.shopattropical.com/api/hr/delete/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      alert("Employee deleted successfully!");
      fetchEmployees();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Failed to delete employee");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "PENDING":
        return (
          <span className="bg-yellow-200 text-yellow-800 px-2 py-1 rounded-full text-xs font-semibold">
            PENDING
          </span>
        );
      case "COMPLETED":
        return (
          <span className="bg-blue-200 text-blue-800 px-2 py-1 rounded-full text-xs font-semibold">
            COMPLETED
          </span>
        );
      case "APPROVED":
        return (
          <span className="bg-green-200 text-green-800 px-2 py-1 rounded-full text-xs font-semibold">
            APPROVED
          </span>
        );
      default:
        return (
          <span className="bg-gray-200 text-gray-800 px-2 py-1 rounded-full text-xs font-semibold">
            {status}
          </span>
        );
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold mb-6">HR Employee Onboarding</h1>

      {/* CREATE / EDIT FORM */}
      <form
        onSubmit={handleSubmit}
        className="bg-white p-4 rounded shadow mb-6 max-w-md"
      >
        <h2 className="text-xl font-semibold mb-4">
          {editingId ? "Edit Employee" : "Add Employee"}
        </h2>
        <input
          name="first_name"
          placeholder="First Name"
          value={form.first_name}
          onChange={handleChange}
          className="border p-2 rounded w-full mb-2"
          required
        />
        <input
          name="last_name"
          placeholder="Last Name"
          value={form.last_name}
          onChange={handleChange}
          className="border p-2 rounded w-full mb-2"
          required
        />
        <input
          name="email"
          placeholder="Email"
          value={form.email}
          onChange={handleChange}
          className="border p-2 rounded w-full mb-2"
          required
        />
        {!editingId && (
          <input
            type="password"
            name="password"
            placeholder="Temporary Password"
            value={form.password}
            onChange={handleChange}
            className="border p-2 rounded w-full mb-2"
            required
          />
        )}
        <button
          type="submit"
          className={`px-4 py-2 rounded text-white ${
            editingId ? "bg-yellow-600" : "bg-blue-600"
          }`}
        >
          {editingId ? "Update Employee" : "Add Employee"}
        </button>
      </form>

      {/* EMPLOYEE TABLE */}
      <table className="w-full bg-white shadow rounded">
        <thead className="bg-gray-200">
          <tr>
            <th className="p-2 border">ID</th>
            <th className="p-2 border">Name</th>
            <th className="p-2 border">Email</th>
            <th className="p-2 border">Status</th>
            <th className="p-2 border">Actions</th>
          </tr>
        </thead>
        <tbody>
          {employees.map((emp) => (
            <tr key={emp.id}>
              <td className="border p-2">{emp.id}</td>
              <td className="border p-2">
                {emp.first_name} {emp.last_name}
              </td>
              <td className="border p-2">{emp.email}</td>
              <td className="border p-2">{getStatusBadge(emp.onboarding_status)}</td>
              <td className="border p-2 flex gap-2">
                {emp.onboarding_status === "COMPLETED" && (
                  <button
                    onClick={() => handleApprove(emp.id)}
                    className="bg-green-600 text-white p-2 rounded"
                    title="Approve"
                  >
                    <FaCheck />
                  </button>
                )}
                <button
                  onClick={() => handleEdit(emp)}
                  className="bg-yellow-500 text-white p-2 rounded"
                  title="Edit"
                >
                  <FaEdit />
                </button>
                <button
                  onClick={() => handleDelete(emp.id)}
                  className="bg-red-600 text-white p-2 rounded"
                  title="Delete"
                >
                  <FaTrash />
                </button>
              </td>
            </tr>
          ))}
          {employees.length === 0 && (
            <tr>
              <td colSpan="5" className="text-center p-4">
                No employees found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
